import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FLOW_ARROW_ENDS,
  FLOW_ARROW_HEADS,
  FLOW_LAYOUTS,
  FLOW_SCHEMES,
  FLOW_SHAPES,
  FLOW_THEMES,
  cloneDocument,
  createFlowNode,
  emptyFlowDocument,
  newFlowId,
  nodeLevel,
  noteColorsForTheme,
  textColorForFill,
  themeLevelStyle,
} from "./flowModel";
import { layoutFlow, linkDragTarget, nextLinkRoute, seedLinkRoute } from "./flowLayout";
import { renderFlowSvg } from "./flowSvg";
import { noteHtmlForEditor, readNoteField } from "./flowNoteMarkup";
import {
  createFlowDiagram,
  deleteFlowDiagram,
  getFlowDiagram,
  listFlowDiagrams,
  updateFlowDiagram,
} from "./flowApi";
import "./flowDiagram.css";

const LINE_COLORS = ["#64748b", "#334155", "#1e40af", "#15803d", "#b45309", "#b91c1c", "#7c3aed", "#0e7490"];

const NOTE_DESIGNS = [
  { id: "card", label: "Card" },
  { id: "plain", label: "Plain" },
  { id: "sidebar", label: "Side" },
  { id: "outline", label: "Frame" },
  { id: "note", label: "Note" },
];

const NOTE_COLORS = [
  "#6b7280",
  "#334155",
  "#1e40af",
  "#15803d",
  "#b45309",
  "#b91c1c",
  "#7c3aed",
  "#ffffff",
  "#f3f4f6",
  "#dbeafe",
  "#dcfce7",
  "#fef9c3",
  "#fee2e2",
  "#f3e8ff",
];

const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
};

function toColorInput(value) {
  const hex = String(value || "#ffffff");
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex.slice(1).split("").map((ch) => ch + ch).join("")}`;
  }
  if (/^#[0-9a-fA-F]{6}$/i.test(hex)) return hex;
  return "#ffffff";
}

function TypeIcon({ type }) {
  if (type === "radial") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="32" cy="10" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="52" cy="32" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="32" cy="54" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="32" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M32 14v13M36 32h12M32 37v13M28 32H16" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    );
  }
  if (type === "leftRight") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="6" y="24" width="16" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="40" y="8" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="40" y="26" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <rect x="40" y="44" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M22 31h10M32 14v36M32 14h8M32 32h8M32 50h8" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <rect x="24" y="6" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="8" y="28" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="40" y="28" width="16" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="4" y="48" width="12" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="20" y="48" width="12" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M32 18v6M16 24v4M48 24v4M16 24h32M10 40v8M26 40v8M16 40h10" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function ArrowGlyph({ head }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      {head === "triangle" && <polygon points="6,7 26,16 6,25" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {head === "diamond" && <polygon points="26,16 16,6 4,16 16,26" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {head === "filled" && <polygon points="26,16 16,6 4,16 16,26" fill="currentColor" stroke="currentColor" strokeWidth="1" />}
      {head === "plus" && (
        <>
          <circle cx="16" cy="16" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M16 11v10M11 16h10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
      {(!head || head === "arrow") && (
        <polyline points="6,8 24,16 6,24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function ShapeGlyph({ shape }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      {shape === "circle" && <ellipse cx="16" cy="16" rx="10" ry="10" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {shape === "triangle" && <polygon points="16,5 28,27 4,27" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {shape === "rhombus" && <polygon points="16,4 28,16 16,28 4,16" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {shape === "rhomboid" && <polygon points="10,6 28,6 22,26 4,26" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {shape === "rectangle" && <rect x="5" y="8" width="22" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" />}
      {(!shape || shape === "rounded") && <rect x="5" y="8" width="22" height="16" rx="4" fill="none" stroke="currentColor" strokeWidth="1.6" />}
    </svg>
  );
}

function linkEndHandle(port, elbow) {
  const dx = (elbow?.x ?? port.x) - port.x;
  const dy = (elbow?.y ?? port.y) - port.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return { x: port.x, y: port.y };
  const dist = Math.min(28, len);
  return { x: port.x + (dx / len) * dist, y: port.y + (dy / len) * dist };
}

function sideDirection(layout, node, parent) {
  if (layout === "leftRight") {
    if (node.branch === "up" || node.branch === "down") return node.branch;
    if (!parent) return "down";
    return node.y + node.h / 2 < parent.y + parent.h / 2 ? "up" : "down";
  }
  if (node.branch === "left" || node.branch === "right") return node.branch;
  if (!parent) return "right";
  return node.x + node.w / 2 < parent.x + parent.w / 2 - 1 ? "left" : "right";
}

function FlowRadios({ name, value, options, onChange, className = "" }) {
  return (
    <div className={`radio-inputs flow-pop-radios${className ? ` ${className}` : ""}`}>
      {options.map((option) => (
        <label key={option.id} className="radio">
          <input
            type="radio"
            name={name}
            checked={value === option.id}
            onChange={() => onChange(option.id)}
          />
          <span className="name">{option.label}</span>
        </label>
      ))}
    </div>
  );
}

function placementForDrop(layout, box, clientX, clientY) {
  if (!box || layout === "radial") return undefined;
  if (layout === "leftRight") {
    const inMainBand = clientX > box.right + 28;
    if (clientY < box.top - 16 && !inMainBand) return "up";
    if (clientY > box.bottom + 16 && !inMainBand) return "down";
    return null;
  }
  if (layout !== "topDown") return undefined;
  const inMainBand = clientY > box.bottom + 28;
  if (clientX < box.left - 16 && !inMainBand) return "left";
  if (clientX > box.right + 16 && !inMainBand) return "right";
  return null;
}

function siblingCoord(layout, branch, node, parent) {
  if (layout === "radial" && parent) {
    return Math.atan2(
      (node.y + node.h / 2) - (parent.y + parent.h / 2),
      (node.x + node.w / 2) - (parent.x + parent.w / 2)
    );
  }
  if (layout === "leftRight") return branch === "up" || branch === "down" ? node.x + node.w / 2 : node.y + node.h / 2;
  if (branch === "left" || branch === "right") return node.y + node.h / 2;
  return node.x + node.w / 2;
}

function pointerCoord(layout, branch, pointer, parent) {
  if (layout === "radial" && parent) {
    return Math.atan2(pointer.y - (parent.y + parent.h / 2), pointer.x - (parent.x + parent.w / 2));
  }
  if (layout === "leftRight") return branch === "up" || branch === "down" ? pointer.x : pointer.y;
  if (branch === "left" || branch === "right") return pointer.y;
  return pointer.x;
}

function documentOrder(list) {
  return list
    .map((node, index) => ({ node, index }))
    .sort((a, b) => {
      const ao = Number(a.node.order);
      const bo = Number(b.node.order);
      const av = Number.isFinite(ao) ? ao : a.index;
      const bv = Number.isFinite(bo) ? bo : b.index;
      return av - bv || a.index - b.index;
    })
    .map((item) => item.node.id);
}

function arrangeNodes(nodes, { layout, draggedId, parentId, branch, pointer, laidNodes }) {
  const parent = laidNodes.find((node) => node.id === parentId);
  const dragged = nodes.find((node) => node.id === draggedId);
  if (!parent || !dragged) return nodes;
  const key = branch || null;
  const others = laidNodes.filter((node) => node.parentId === parentId && node.id !== draggedId && (node.branch || null) === key);
  const at = pointerCoord(layout, key, pointer, parent);
  const ordered = others.slice().sort((a, b) => siblingCoord(layout, key, a, parent) - siblingCoord(layout, key, b, parent));
  let index = ordered.findIndex((node) => siblingCoord(layout, key, node, parent) > at);
  if (index < 0) index = ordered.length;
  const ids = [...ordered.slice(0, index), { id: draggedId }, ...ordered.slice(index)].map((node) => node.id);
  const prevKey = dragged.branch || null;
  const previous = documentOrder(nodes.filter((node) => node.parentId === parentId && (node.branch || null) === prevKey));
  if (prevKey === key && previous.join("|") === ids.join("|")) return nodes;
  const orderOf = new Map(ids.map((id, itemIndex) => [id, itemIndex]));
  return nodes.map((node) => {
    if (node.id === draggedId) {
      const next = { ...node, order: orderOf.get(node.id) };
      if (key == null) delete next.branch;
      else next.branch = key;
      return next;
    }
    if (!orderOf.has(node.id)) return node;
    return { ...node, order: orderOf.get(node.id) };
  });
}

function connectorSegment(points, x, y) {
  const segments = [];
  let best = null;
  let bestDist = 20;
  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1];
    const end = points[index];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = dx * dx + dy * dy;
    if (length < 4) continue;
    const axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
    segments.push({ index, axis });
    const t = Math.max(0, Math.min(1, ((x - start.x) * dx + (y - start.y) * dy) / length));
    const dist = Math.hypot(x - (start.x + dx * t), y - (start.y + dy * t));
    if (dist < bestDist) {
      bestDist = dist;
      best = { index, axis };
    }
  }
  if (!best) return null;
  const same = segments.filter((segment) => segment.axis === best.axis);
  let role = "bar";
  if (same.length > 1 && best.index === same[0].index) role = "stem";
  else if (same.length > 1 && best.index === same[same.length - 1].index) role = "stub";
  return { axis: best.axis, role };
}

function segmentSpan(drag, delta) {
  const next = {
    spanX: drag.startSpanX,
    spanY: drag.startSpanY,
    bendX: drag.startBendX,
    bendY: drag.startBendY,
  };
  if (drag.role === "stem") {
    if (drag.axis === "x") {
      next.spanX += delta;
      next.bendX += delta / 2;
    } else {
      next.spanY += delta;
      next.bendY += delta / 2;
    }
  } else if (drag.role === "stub") {
    if (drag.axis === "x") {
      next.spanX += delta;
      next.bendX -= delta / 2;
    } else {
      next.spanY += delta;
      next.bendY -= delta / 2;
    }
  } else if (drag.axis === "x") {
    next.spanX += delta;
  } else {
    next.spanY += delta;
  }
  return next;
}

function writeSegment(node, span) {
  const next = { ...node };
  const fields = [
    ["spanX", span.spanX],
    ["spanY", span.spanY],
    ["bendX", span.bendX],
    ["bendY", span.bendY],
  ];
  fields.forEach(([key, value]) => {
    const rounded = Math.round(value);
    if (rounded) next[key] = rounded;
    else delete next[key];
  });
  return next;
}

const ALIGN_SNAP = 12;
const GUIDE_PAD = 16;
const NOTE_MIN_W = 180;
const NOTE_MAX_W = 1200;
const NOTE_MIN_H = 120;
const NOTE_MAX_H = 1200;

function edgeAnchors(start, size) {
  return [start, start + size / 2, start + size];
}

function snapFreeBox(box, others) {
  let bestX = null;
  let bestXDist = ALIGN_SNAP + 1;
  let guideX = null;
  let bestY = null;
  let bestYDist = ALIGN_SNAP + 1;
  let guideY = null;
  const xPoints = edgeAnchors(box.x, box.w);
  const yPoints = edgeAnchors(box.y, box.h);
  others.forEach((other) => {
    edgeAnchors(other.x, other.w).forEach((target) => {
      xPoints.forEach((point) => {
        const dist = Math.abs(point - target);
        if (dist <= ALIGN_SNAP && dist < bestXDist) {
          bestXDist = dist;
          bestX = box.x + (target - point);
          guideX = target;
        }
      });
    });
    edgeAnchors(other.y, other.h).forEach((target) => {
      yPoints.forEach((point) => {
        const dist = Math.abs(point - target);
        if (dist <= ALIGN_SNAP && dist < bestYDist) {
          bestYDist = dist;
          bestY = box.y + (target - point);
          guideY = target;
        }
      });
    });
  });
  const x = bestX == null ? box.x : bestX;
  const y = bestY == null ? box.y : bestY;
  return { x, y, ...alignmentGuides({ x, y, w: box.w, h: box.h }, others, guideX, guideY) };
}

function alignmentGuides(box, others, guideX, guideY) {
  let vertical = null;
  let horizontal = null;
  if (guideX != null) {
    let y1 = box.y;
    let y2 = box.y + box.h;
    others.forEach((other) => {
      if (!edgeAnchors(other.x, other.w).some((point) => Math.abs(point - guideX) < 0.5)) return;
      y1 = Math.min(y1, other.y);
      y2 = Math.max(y2, other.y + other.h);
    });
    vertical = { x: guideX, y1: y1 - GUIDE_PAD, y2: y2 + GUIDE_PAD };
  }
  if (guideY != null) {
    let x1 = box.x;
    let x2 = box.x + box.w;
    others.forEach((other) => {
      if (!edgeAnchors(other.y, other.h).some((point) => Math.abs(point - guideY) < 0.5)) return;
      x1 = Math.min(x1, other.x);
      x2 = Math.max(x2, other.x + other.w);
    });
    horizontal = { y: guideY, x1: x1 - GUIDE_PAD, x2: x2 + GUIDE_PAD };
  }
  return { vertical, horizontal };
}

function snapResizeBox(box, others) {
  let bestW = null;
  let bestWDist = ALIGN_SNAP + 1;
  let guideX = null;
  let bestH = null;
  let bestHDist = ALIGN_SNAP + 1;
  let guideY = null;
  const right = box.x + box.w;
  const bottom = box.y + box.h;
  others.forEach((other) => {
    edgeAnchors(other.x, other.w).forEach((target) => {
      const width = target - box.x;
      const dist = Math.abs(right - target);
      if (dist <= ALIGN_SNAP && dist < bestWDist && width >= NOTE_MIN_W && width <= NOTE_MAX_W) {
        bestWDist = dist;
        bestW = width;
        guideX = target;
      }
    });
    edgeAnchors(other.y, other.h).forEach((target) => {
      const height = target - box.y;
      const dist = Math.abs(bottom - target);
      if (dist <= ALIGN_SNAP && dist < bestHDist && height >= NOTE_MIN_H && height <= NOTE_MAX_H) {
        bestHDist = dist;
        bestH = height;
        guideY = target;
      }
    });
  });
  const next = {
    x: box.x,
    y: box.y,
    w: bestW == null ? box.w : bestW,
    h: bestH == null ? box.h : bestH,
  };
  return { ...next, ...alignmentGuides(next, others, guideX, guideY) };
}

function shiftedSnapGuides(snapped, shift) {
  const shiftX = shift?.x || 0;
  const shiftY = shift?.y || 0;
  return {
    vertical: snapped.vertical
      ? {
        x: snapped.vertical.x + shiftX,
        y1: snapped.vertical.y1 + shiftY,
        y2: snapped.vertical.y2 + shiftY,
      }
      : null,
    horizontal: snapped.horizontal
      ? {
        y: snapped.horizontal.y + shiftY,
        x1: snapped.horizontal.x1 + shiftX,
        x2: snapped.horizontal.x2 + shiftX,
      }
      : null,
  };
}

const LABEL_LINE_SNAP = 12;

function closestPointOnSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / len2)) : 0;
  const x = a.x + dx * t;
  const y = a.y + dy * t;
  return { x, y, dist: Math.hypot(point.x - x, point.y - y) };
}

function snapLabelToLine(box, edges) {
  const center = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
  const reach = Math.min(box.w, box.h) / 2 + LABEL_LINE_SNAP;
  let best = null;
  edges.forEach((edge) => {
    const points = edge.points;
    if (!points || points.length < 2) return;
    for (let i = 1; i < points.length; i += 1) {
      const hit = closestPointOnSegment(center, points[i - 1], points[i]);
      if (hit.dist <= reach && (!best || hit.dist < best.dist)) best = hit;
    }
  });
  if (!best) return null;
  const x = best.x - box.w / 2;
  const y = best.y - box.h / 2;
  return {
    x,
    y,
    vertical: { x: best.x, y1: y - GUIDE_PAD, y2: y + box.h + GUIDE_PAD },
    horizontal: { y: best.y, x1: x - GUIDE_PAD, x2: x + box.w + GUIDE_PAD },
  };
}

function clampSpanDelta(delta, drag) {
  const pad = 28;
  const limit = 1600;
  if (drag.axis === "x") {
    const startLeft = drag.childX;
    const startRight = drag.childX + drag.childW;
    let next = startLeft + delta;
    if (startLeft >= drag.parentX + drag.parentW - 4) next = Math.max(drag.parentX + drag.parentW + pad, next);
    else if (startRight <= drag.parentX + 4) next = Math.min(drag.parentX - pad - drag.childW, next);
    return Math.max(-limit, Math.min(limit, next - startLeft));
  }
  const startTop = drag.childY;
  const startBottom = drag.childY + drag.childH;
  let next = startTop + delta;
  if (startTop >= drag.parentY + drag.parentH - 4) next = Math.max(drag.parentY + drag.parentH + pad, next);
  else if (startBottom <= drag.parentY + 4) next = Math.min(drag.parentY - pad - drag.childH, next);
  return Math.max(-limit, Math.min(limit, next - startTop));
}

const NOTE_INLINE = /^(B|STRONG|I|EM|U|SPAN|FONT|A|MARK|S|STRIKE|SUB|SUP|CODE|SMALL)$/;

function noteCaretRange(root) {
  const sel = window.getSelection();
  if (!sel) return null;
  if (!sel.rangeCount || !root.contains(sel.anchorNode)) {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  return range;
}

/** Move a collapsed caret out of bold, italic, underline, and other inline styling. */
function liftCaretOutOfInline(root) {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return;
  let guard = 0;
  while (guard < 16) {
    guard += 1;
    const range = sel.getRangeAt(0);
    if (!range.collapsed || !root.contains(range.startContainer)) return;
    let host = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentNode;
    if (!host || host === root) return;
    let wrap = null;
    while (host && host !== root) {
      if (host.nodeType === 1 && NOTE_INLINE.test(host.tagName)) wrap = host;
      host = host.parentNode;
    }
    if (!wrap) return;
    const tail = document.createRange();
    tail.setStart(range.startContainer, range.startOffset);
    tail.setEnd(wrap, wrap.childNodes.length);
    if (tail.collapsed) {
      const next = document.createRange();
      next.setStartAfter(wrap);
      next.collapse(true);
      sel.removeAllRanges();
      sel.addRange(next);
      continue;
    }
    const after = wrap.cloneNode(false);
    after.appendChild(tail.extractContents());
    wrap.after(after);
    if (!wrap.textContent && !wrap.querySelector("br")) wrap.remove();
    const next = document.createRange();
    if (after.isConnected) next.setStartBefore(after);
    else {
      next.selectNodeContents(root);
      next.collapse(false);
    }
    next.collapse(true);
    sel.removeAllRanges();
    sel.addRange(next);
    if (after.isConnected && !after.textContent && !after.querySelector("br")) after.remove();
  }
}

function insertPlainNoteText(root, text, singleLine) {
  const normalized = singleLine
    ? String(text || "").replace(/[\r\n\u2028\u2029]+/g, " ")
    : String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!normalized) return;
  root.focus();
  const range = noteCaretRange(root);
  if (!range) return;
  range.deleteContents();
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
  liftCaretOutOfInline(root);
  if (!sel?.rangeCount) return;
  const dest = sel.getRangeAt(0);
  const lines = singleLine ? [normalized] : normalized.split("\n");
  const frag = document.createDocumentFragment();
  let last = null;
  lines.forEach((line, index) => {
    if (index) {
      last = document.createElement("br");
      frag.appendChild(last);
    }
    if (line) {
      last = document.createTextNode(line.replace(/\u200b/g, ""));
      frag.appendChild(last);
    }
  });
  if (last?.tagName === "BR") {
    last = document.createTextNode("\u200b");
    frag.appendChild(last);
  }
  if (!last) return;
  dest.insertNode(frag);
  const after = document.createRange();
  after.setStartAfter(last);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
}

function insertNoteLineBreak(root) {
  root.focus();
  const range = noteCaretRange(root);
  if (!range) return;
  if (!range.collapsed) range.deleteContents();
  const br = document.createElement("br");
  range.insertNode(br);
  let caret = br;
  if (!br.nextSibling) {
    caret = document.createTextNode("\u200b");
    br.after(caret);
  }
  const after = document.createRange();
  if (caret === br) after.setStartAfter(br);
  else after.setStart(caret, caret.length);
  after.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(after);
}

function applyNoteShortcut(event) {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return false;
  const key = String(event.key || "").toLowerCase();
  const command = key === "b" ? "bold" : key === "i" ? "italic" : key === "u" ? "underline" : "";
  if (!command) return false;
  event.preventDefault();
  event.stopPropagation();
  try {
    document.execCommand("styleWithCSS", false, false);
  } catch {
    /* older editors ignore this */
  }
  document.execCommand(command);
  return true;
}

function NoteRichText({
  value,
  onChange,
  className,
  style,
  placeholder,
  ariaLabel,
  maxLength,
  singleLine,
  onFocus,
}) {
  const ref = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const html = noteHtmlForEditor(value);
    if (document.activeElement === el && readNoteField(el, maxLength, singleLine) === value) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [value, maxLength, singleLine]);

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    const next = readNoteField(el, maxLength, singleLine);
    if (next === valueRef.current) return;
    onChange(next);
  };

  return (
    <div
      ref={ref}
      className={className}
      style={style}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline={singleLine ? "false" : "true"}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      spellCheck
      onFocus={onFocus}
      onInput={commit}
      onBlur={(event) => {
        const el = event.currentTarget;
        if (!el.textContent) el.innerHTML = "";
        commit();
      }}
      onPaste={(event) => {
        event.preventDefault();
        const text = event.clipboardData?.getData("text/plain") || "";
        insertPlainNoteText(event.currentTarget, text, singleLine);
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          if (!singleLine) {
            insertNoteLineBreak(event.currentTarget);
            commit();
          }
          return;
        }
        if (applyNoteShortcut(event)) commit();
      }}
    />
  );
}

export default function FlowDiagramModal({ show, embedId, onHide, onApply }) {
  const [phase, setPhase] = useState("pick");
  const [layout, setLayout] = useState("topDown");
  const [nodes, setNodes] = useState(() => emptyFlowDocument().nodes);
  const [edges, setEdges] = useState({});
  const [links, setLinks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [noteSection, setNoteSection] = useState(null);
  const [noteColorDetail, setNoteColorDetail] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [diagramId, setDiagramId] = useState(null);
  const [title, setTitle] = useState("");
  const [isOwner, setIsOwner] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsFor, setSettingsFor] = useState(null);
  const [lineEdit, setLineEdit] = useState(null);
  const [connectMode, setConnectMode] = useState(false);
  const [spanMode, setSpanMode] = useState(false);
  const [connectFrom, setConnectFrom] = useState(null);
  const [titleOpen, setTitleOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [titleIntent, setTitleIntent] = useState("save");
  const [nudge, setNudge] = useState(null);
  const [shiftLock, setShiftLock] = useState(null);
  const [dragKind, setDragKind] = useState(null);
  const [snapGuides, setSnapGuides] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [panning, setPanning] = useState(false);
  const [themeId, setThemeId] = useState(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const stageRef = useRef(null);
  const scrollRef = useRef(null);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  const dragRef = useRef(null);
  const laidRef = useRef(null);
  const skipClickRef = useRef(false);
  const historyRef = useRef({ stack: [], index: -1 });
  const historyTimerRef = useRef(0);
  const historyResetRef = useRef(false);
  const captureSavedRef = useRef(false);
  const restoringRef = useRef(false);
  const savedSnapRef = useRef(null);
  const flushHistoryRef = useRef(() => {});
  const [historyTick, setHistoryTick] = useState(0);

  const laid = useMemo(
    () => layoutFlow(layout, { nodes, edges, links, labels, notes }, { nudge, shiftLock }),
    [layout, nodes, edges, links, labels, notes, nudge, shiftLock]
  );
  laidRef.current = laid;
  const diagramSnap = useMemo(
    () => stableStringify({ layout, nodes, edges, links, labels, notes, theme: themeId || null }),
    [layout, nodes, edges, links, labels, notes, themeId]
  );
  const svg = useMemo(
    () => renderFlowSvg({ layout, document: { nodes, edges, links, labels, notes } }, { interactive: true, laid }),
    [layout, nodes, edges, links, labels, notes, laid]
  );

  const applyRecord = (row) => {
    const doc = cloneDocument(row.document);
    historyResetRef.current = true;
    captureSavedRef.current = true;
    setDiagramId(row.id);
    setTitle(row.title || "");
    setIsOwner(row.isOwner !== false);
    setLayout(row.layout || "topDown");
    setNodes(doc.nodes.length ? doc.nodes : emptyFlowDocument().nodes);
    setEdges(doc.edges);
    setLinks(doc.links);
    setLabels(doc.labels || []);
    setNotes(doc.notes || []);
    setNoteFor(null);
    setNoteSection(null);
    setThemeId(doc.theme || null);
    setToolsOpen(false);
    setPhase("edit");
    setZoom(1);
    setDirty(false);
    setSettingsFor(null);
    setLineEdit(null);
    setConnectMode(false);
    setSpanMode(false);
    setConnectFrom(null);
  };

  useEffect(() => () => dragRef.current?.stop?.(), []);

  useEffect(() => {
    if (!editingLabel) return undefined;
    const field = stageRef.current?.querySelector(`[data-label-id="${editingLabel}"]`);
    field?.focus();
    return undefined;
  }, [editingLabel]);

  useEffect(() => {
    if (!show) return undefined;
    let cancel = false;
    setPhase("pick");
    setDiagramId(null);
    setTitle("");
    setIsOwner(true);
    setDirty(false);
    setSettingsFor(null);
    setLineEdit(null);
    setConnectMode(false);
    setSpanMode(false);
    setConnectFrom(null);
    setLabels([]);
    setNotes([]);
    setNoteFor(null);
    setNoteSection(null);
    setEditingLabel(null);
    setThemeId(null);
    setToolsOpen(false);
    setZoom(1);
    setNudge(null);
    setShiftLock(null);
    setDragKind(null);
    setTitleOpen(false);
    setLoading(true);
    (async () => {
      try {
        const rows = await listFlowDiagrams();
        if (!cancel) setSaved(Array.isArray(rows) ? rows : []);
        if (embedId) {
          const row = await getFlowDiagram(embedId);
          if (!cancel) applyRecord(row);
        }
      } catch (err) {
        if (!cancel) toast.error(err.message || "Could not load diagrams");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [show, embedId]);

  useEffect(() => {
    if (!show || (!settingsFor && !lineEdit && !noteFor)) return undefined;
    const closeIfOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".flow-pop, .flow-node-gear, .flow-line-btn, .flow-note")) return;
      setSettingsFor(null);
      setLineEdit(null);
      setNoteFor(null);
    };
    document.addEventListener("pointerdown", closeIfOutside, true);
    return () => document.removeEventListener("pointerdown", closeIfOutside, true);
  }, [show, settingsFor, lineEdit, noteFor]);

  useEffect(() => {
    if (!titleOpen) return undefined;
    const frame = requestAnimationFrame(() => {
      const backs = document.querySelectorAll(".modal-backdrop");
      const latest = backs[backs.length - 1];
      const modal = document.querySelector(".flow-title-modal");
      if (latest) latest.style.zIndex = "1080";
      if (modal) modal.style.zIndex = "1085";
    });
    return () => cancelAnimationFrame(frame);
  }, [titleOpen]);

  const markDirty = () => setDirty(true);

  const clearHistoryTimer = () => {
    if (historyTimerRef.current) {
      window.clearTimeout(historyTimerRef.current);
      historyTimerRef.current = 0;
    }
  };

  const commitDiagramSnap = (json) => {
    const hist = historyRef.current;
    if (!json || (hist.index >= 0 && hist.stack[hist.index] === json)) return;
    const next = hist.stack.slice(0, Math.max(0, hist.index + 1));
    next.push(json);
    while (next.length > 80) next.shift();
    hist.stack = next;
    hist.index = next.length - 1;
    setHistoryTick((tick) => tick + 1);
  };

  const applyDiagramSnap = (json) => {
    const doc = JSON.parse(json);
    restoringRef.current = true;
    setLayout(doc.layout || "topDown");
    setNodes(Array.isArray(doc.nodes) && doc.nodes.length ? doc.nodes : emptyFlowDocument().nodes);
    setEdges(doc.edges || {});
    setLinks(doc.links || []);
    setLabels(doc.labels || []);
    setNotes(doc.notes || []);
    setThemeId(doc.theme || null);
    setNudge(null);
    setShiftLock(null);
    setSettingsFor(null);
    setLineEdit(null);
    setNoteFor(null);
    setEditingLabel(null);
    setConnectMode(false);
    setSpanMode(false);
    setConnectFrom(null);
    setDirty(savedSnapRef.current == null || json !== savedSnapRef.current);
    setHistoryTick((tick) => tick + 1);
  };

  const performDiagramUndo = () => {
    clearHistoryTimer();
    const hist = historyRef.current;
    if (hist.index < 0) return;
    if (diagramSnap !== hist.stack[hist.index]) commitDiagramSnap(diagramSnap);
    if (hist.index <= 0) return;
    hist.index -= 1;
    applyDiagramSnap(hist.stack[hist.index]);
  };

  const performDiagramRedo = () => {
    clearHistoryTimer();
    const hist = historyRef.current;
    if (hist.index < 0) return;
    if (diagramSnap !== hist.stack[hist.index]) {
      commitDiagramSnap(diagramSnap);
      return;
    }
    if (hist.index >= hist.stack.length - 1) return;
    hist.index += 1;
    applyDiagramSnap(hist.stack[hist.index]);
  };

  flushHistoryRef.current = () => {
    if (restoringRef.current || historyResetRef.current || dragRef.current) return;
    commitDiagramSnap(diagramSnap);
  };

  useEffect(() => {
    if (!show || phase !== "edit") return undefined;
    if (historyResetRef.current) {
      historyResetRef.current = false;
      historyRef.current = { stack: [diagramSnap], index: 0 };
      if (captureSavedRef.current) {
        savedSnapRef.current = diagramSnap;
        captureSavedRef.current = false;
      }
      setHistoryTick((tick) => tick + 1);
      return undefined;
    }
    if (restoringRef.current) {
      restoringRef.current = false;
      const hist = historyRef.current;
      if (hist.index >= 0 && hist.stack[hist.index] !== diagramSnap) {
        hist.stack[hist.index] = diagramSnap;
        setHistoryTick((tick) => tick + 1);
      }
      return undefined;
    }
    clearHistoryTimer();
    historyTimerRef.current = window.setTimeout(() => {
      historyTimerRef.current = 0;
      if (dragRef.current) return;
      if (restoringRef.current || historyResetRef.current) return;
      commitDiagramSnap(diagramSnap);
    }, 320);
    return () => clearHistoryTimer();
  }, [diagramSnap, show, phase]);

  useEffect(() => {
    if (!show || phase !== "edit") return undefined;
    const onKey = (event) => {
      if (titleOpen) return;
      const target = event.target;
      if (!(target instanceof Element) || !target.closest(".flow-modal, .flow-pop")) return;
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      const key = String(event.key || "").toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        performDiagramUndo();
        return;
      }
      if (key === "y" || (key === "z" && event.shiftKey)) {
        event.preventDefault();
        performDiagramRedo();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, phase, titleOpen, diagramSnap, historyTick]);

  const changeZoom = (next) => {
    const prev = zoomRef.current || 1;
    const clamped = Math.min(2, Math.max(0.25, Math.round(next * 10) / 10));
    const scroller = scrollRef.current;
    if (!scroller || clamped === prev) {
      setZoom(clamped);
      return;
    }
    const anchorX = (scroller.scrollLeft + scroller.clientWidth / 2) / prev;
    const anchorY = (scroller.scrollTop + scroller.clientHeight / 2) / prev;
    setZoom(clamped);
    window.requestAnimationFrame(() => {
      scroller.scrollLeft = anchorX * clamped - scroller.clientWidth / 2;
      scroller.scrollTop = anchorY * clamped - scroller.clientHeight / 2;
    });
  };

  const beginPan = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest(".flow-node-editor, .flow-plus, .flow-line-btn, .flow-pop, .flow-link-hit, .flow-branch-hit, .flow-link-end, .flow-label, .flow-note, .flow-zoom, .flow-history, .flow-canvas-tools")) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    setSettingsFor(null);
    setLineEdit(null);
    setNoteFor(null);
    event.preventDefault();
    const originX = event.clientX;
    const originY = event.clientY;
    const originLeft = scroller.scrollLeft;
    const originTop = scroller.scrollTop;
    setPanning(true);
    const move = (ev) => {
      scroller.scrollLeft = originLeft - (ev.clientX - originX);
      scroller.scrollTop = originTop - (ev.clientY - originY);
    };
    const up = () => {
      setPanning(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const startLayout = (nextLayout) => {
    historyResetRef.current = true;
    captureSavedRef.current = false;
    savedSnapRef.current = null;
    setLayout(nextLayout);
    setNodes([createFlowNode(null)]);
    setEdges({});
    setLinks([]);
    setLabels([]);
    setNotes([]);
    setNoteFor(null);
    setNoteSection(null);
    setEditingLabel(null);
    setThemeId(null);
    setToolsOpen(false);
    setZoom(1);
    setDiagramId(null);
    setTitle("");
    setIsOwner(true);
    setDirty(true);
    setPhase("edit");
    setSettingsFor(null);
    setLineEdit(null);
    setSpanMode(false);
  };

  const backToPicker = () => {
    if (dirty && !window.confirm("Leave this diagram? Unsaved changes on the canvas will be lost.")) return;
    setPhase("pick");
    setConnectMode(false);
    setSpanMode(false);
    setConnectFrom(null);
    setSettingsFor(null);
    setLineEdit(null);
    setNoteFor(null);
    setToolsOpen(false);
  };

  const updateNode = (id, patch) => {
    setNodes((prev) => {
      const next = prev.map((node) => {
        if (node.id !== id) return node;
        const updated = { ...node, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, "branch") && patch.branch == null) delete updated.branch;
        if (Object.prototype.hasOwnProperty.call(patch, "out") && patch.out == null) {
          delete updated.out;
          if (node.branch && node.branch === node.out) delete updated.branch;
        }
        return updated;
      });
      if (!Object.prototype.hasOwnProperty.call(patch, "out") || !patch.out) return next;
      const byParent = new Map();
      next.forEach((node) => {
        if (!byParent.has(node.parentId)) byParent.set(node.parentId, []);
        byParent.get(node.parentId).push(node);
      });
      const stamp = (parentId) => {
        (byParent.get(parentId) || []).forEach((node) => {
          if (node.out || node.branch) return;
          const index = next.indexOf(node);
          if (index < 0) return;
          next[index] = { ...node, out: patch.out, branch: patch.out };
          stamp(node.id);
        });
      };
      stamp(id);
      return next;
    });
    markDirty();
  };

  const trackPointer = (drag) => {
    dragRef.current = drag;
    const move = (ev) => {
      const current = dragRef.current;
      if (!current || current !== drag) return;
      const scale = zoomRef.current || 1;
      const dx = (ev.clientX - current.startX) / scale;
      const dy = (ev.clientY - current.startY) / scale;
      if (!current.moved) {
        const threshold = current.editing ? 16 : 8;
        if (Math.hypot(dx, dy) < threshold) return;
        current.moved = true;
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        window.getSelection()?.removeAllRanges();
        current.shift = laidRef.current.shift;
        setShiftLock(current.shift);
        setDragKind(current.kind);
      }
      ev.preventDefault();
      if (current.kind === "node") {
        setNudge({ id: current.id, dx, dy });
        return;
      }
      if (current.kind === "label") {
        const shift = current.shift || { x: 0, y: 0 };
        const raw = {
          x: current.originX + dx + shift.x,
          y: current.originY + dy + shift.y,
          w: current.boxW || 72,
          h: current.boxH || 24,
        };
        const lines = [...(laidRef.current?.treeEdges || []), ...(laidRef.current?.links || [])];
        const snapped = snapLabelToLine(raw, lines);
        setSnapGuides(snapped ? { vertical: snapped.vertical, horizontal: snapped.horizontal } : null);
        setLabels((prev) => prev.map((label) => (
          label.id === current.id
            ? {
              ...label,
              x: (snapped ? snapped.x : raw.x) - shift.x,
              y: (snapped ? snapped.y : raw.y) - shift.y,
            }
            : label
        )));
        return;
      }
      if (current.kind === "free") {
        const shift = current.shift || { x: 0, y: 0 };
        const raw = {
          x: current.originX + dx,
          y: current.originY + dy,
          w: current.boxW,
          h: current.boxH,
        };
        const laidNodes = laidRef.current?.nodes || [];
        const descendants = new Set();
        const collectDescendants = (id) => {
          laidNodes.forEach((item) => {
            if (item.parentId !== id || descendants.has(item.id)) return;
            descendants.add(item.id);
            collectDescendants(item.id);
          });
        };
        collectDescendants(current.id);
        const others = laidNodes
          .filter((item) => item.id !== current.id && !descendants.has(item.id))
          .map((item) => ({
            x: item.x - shift.x,
            y: item.y - shift.y,
            w: item.w,
            h: item.h,
          }));
        const snapped = snapFreeBox(raw, others);
        setSnapGuides(shiftedSnapGuides(snapped, shift));
        setNodes((prev) => prev.map((node) => (
          node.id === current.id
            ? { ...node, x: snapped.x, y: snapped.y }
            : node
        )));
        return;
      }
      if (current.kind === "note") {
        const shift = current.shift || { x: 0, y: 0 };
        const raw = {
          x: current.originX + dx,
          y: current.originY + dy,
          w: current.boxW,
          h: current.boxH,
        };
        const laidNow = laidRef.current;
        const others = [
          ...(laidNow?.notes || [])
            .filter((note) => note.id !== current.id)
            .map((note) => ({
              x: note.x - shift.x,
              y: note.y - shift.y,
              w: note.w,
              h: note.h,
            })),
          ...(laidNow?.nodes || []).map((node) => ({
            x: node.x - shift.x,
            y: node.y - shift.y,
            w: node.w,
            h: node.h,
          })),
        ];
        const snapped = snapFreeBox(raw, others);
        setSnapGuides(shiftedSnapGuides(snapped, shift));
        setNotes((prev) => prev.map((note) => (
          note.id === current.id
            ? { ...note, x: snapped.x, y: snapped.y }
            : note
        )));
        return;
      }
      if (current.kind === "note-resize") {
        const shift = current.shift || { x: 0, y: 0 };
        const raw = {
          x: current.originX,
          y: current.originY,
          w: Math.max(NOTE_MIN_W, Math.min(NOTE_MAX_W, current.originW + dx)),
          h: Math.max(NOTE_MIN_H, Math.min(NOTE_MAX_H, current.originH + dy)),
        };
        const laidNow = laidRef.current;
        const others = [
          ...(laidNow?.notes || [])
            .filter((note) => note.id !== current.id)
            .map((note) => ({
              x: note.x - shift.x,
              y: note.y - shift.y,
              w: note.w,
              h: note.h,
            })),
          ...(laidNow?.nodes || []).map((node) => ({
            x: node.x - shift.x,
            y: node.y - shift.y,
            w: node.w,
            h: node.h,
          })),
        ];
        const snapped = snapResizeBox(raw, others);
        setSnapGuides(shiftedSnapGuides(snapped, shift));
        setNotes((prev) => prev.map((note) => (
          note.id === current.id
            ? { ...note, w: snapped.w, h: snapped.h }
            : note
        )));
        return;
      }
      if (current.kind === "span") {
        const raw = current.axis === "x" ? dx : dy;
        if (!current.leftHome && Math.abs(raw) >= 16) current.leftHome = true;
        const delta = current.leftHome && Math.abs(raw) < 14 ? 0 : clampSpanDelta(raw, current);
        const span = segmentSpan(current, delta);
        setNodes((prev) => prev.map((node) => (node.id === current.id ? writeSegment(node, span) : node)));
        return;
      }
      const stage = stageRef.current;
      const laidNow = laidRef.current;
      if (!stage || !laidNow) return;
      const rect = stage.getBoundingClientRect();
      setLinks((prev) => prev.map((link) => {
        if (link.id !== current.id) return link;
        const fromNode = laidNow.nodes.find((node) => node.id === link.fromId);
        const toNode = laidNow.nodes.find((node) => node.id === link.toId);
        if (!fromNode || !toNode) return link;
        if (!current.route) {
          const laidLink = laidNow.links.find((item) => item.id === link.id);
          const points = laidLink?.points?.length ? laidLink.points : link.points;
          current.route = seedLinkRoute(fromNode, toNode, points, current.shift);
        }
        const route = nextLinkRoute({
          fromNode,
          toNode,
          route: current.route,
          part: current.part,
          x: (ev.clientX - rect.left) / (zoomRef.current || 1),
          y: (ev.clientY - rect.top) / (zoomRef.current || 1),
          shift: current.shift,
        });
        current.route = route;
        return { ...link, route };
      }));
    };
    const up = (ev) => {
      const current = dragRef.current;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (!current || current !== drag) return;
      dragRef.current = null;
      const moved = current.moved;
      if (moved) window.setTimeout(() => flushHistoryRef.current(), 0);
      setNudge(null);
      setShiftLock(null);
      setDragKind(null);
      setSnapGuides(null);
      if (current.kind === "node") {
        if (!moved) {
          if (current.edgeKey) {
            setLineEdit({ kind: "tree", key: current.edgeKey });
            setSettingsFor(null);
          }
          return;
        }
        const laidNow = laidRef.current;
        const stage = stageRef.current;
        const existing = laidNow?.nodes.find((node) => node.id === current.id);
        if (!laidNow || !stage || !existing?.parentId) return;
        const rect = stage.getBoundingClientRect();
        const dropped = placementForDrop(current.layout, current.parentBox, ev.clientX, ev.clientY);
        const branch = dropped === undefined ? (existing.branch || null) : dropped;
        let changed = false;
        setNodes((prev) => {
          const next = arrangeNodes(prev, {
            layout: current.layout,
            draggedId: current.id,
            parentId: existing.parentId,
            branch,
            pointer: {
              x: (ev.clientX - rect.left) / (zoomRef.current || 1),
              y: (ev.clientY - rect.top) / (zoomRef.current || 1),
            },
            laidNodes: laidNow.nodes,
          });
          changed = next !== prev;
          return next;
        });
        if (changed) setDirty(true);
        return;
      }
      if (current.kind === "label" || current.kind === "note") {
        if (!moved) current.focus?.focus();
        else setDirty(true);
        return;
      }
      if (current.kind === "free") {
        if (!moved) current.focus?.focus();
        else setDirty(true);
        return;
      }
      if (current.kind === "note-resize") {
        if (moved) setDirty(true);
        return;
      }
      if (current.kind === "span") {
        if (!moved) return;
        setNodes((prev) => prev.map((node) => (
          node.id === current.id
            ? writeSegment(node, {
              spanX: Number(node.spanX) || 0,
              spanY: Number(node.spanY) || 0,
              bendX: Number(node.bendX) || 0,
              bendY: Number(node.bendY) || 0,
            })
            : node
        )));
        setDirty(true);
        return;
      }
      if (!moved) {
        setLineEdit({ kind: "link", id: current.id });
        setSettingsFor(null);
        return;
      }
      if (current.fromButton) skipClickRef.current = true;
      setDirty(true);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    drag.stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  };

  const branchDragBox = (nodeId) => {
    const node = laid.nodes.find((item) => item.id === nodeId);
    const parent = node && laid.nodes.find((item) => item.id === node.parentId);
    const stage = stageRef.current;
    if (!node || !parent || !stage || layout === "radial") return null;
    const rect = stage.getBoundingClientRect();
    const scale = zoom || 1;
    return {
      left: rect.left + parent.x * scale,
      right: rect.left + (parent.x + parent.w) * scale,
      top: rect.top + parent.y * scale,
      bottom: rect.top + (parent.y + parent.h) * scale,
    };
  };

  const beginNodeDrag = (event, node) => {
    const text = event.currentTarget.querySelector?.(".flow-node-text");
    if (text && (event.target === text || text.contains(event.target) || document.activeElement === text)) return;
    const parentBox = layout === "radial" ? null : branchDragBox(node.id);
    if (layout !== "radial" && !parentBox) return;
    event.preventDefault();
    trackPointer({
      kind: "node",
      id: node.id,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      editing: false,
      focus: text,
      layout,
      parentBox,
    });
  };

  const beginSpanDrag = (event, edge) => {
    const stage = stageRef.current;
    const child = laid.nodes.find((node) => node.id === edge.toId);
    const parent = child && laid.nodes.find((node) => node.id === child.parentId);
    if (!stage || !child || !parent) return;
    const rect = stage.getBoundingClientRect();
    const scale = zoomRef.current || 1;
    const segment = connectorSegment(edge.points, (event.clientX - rect.left) / scale, (event.clientY - rect.top) / scale);
    if (!segment) return;
    event.preventDefault();
    const stored = nodes.find((node) => node.id === child.id) || child;
    trackPointer({
      kind: "span",
      id: child.id,
      axis: segment.axis,
      role: segment.role,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      leftHome: false,
      startSpanX: Number(stored.spanX) || 0,
      startSpanY: Number(stored.spanY) || 0,
      startBendX: Number(stored.bendX) || 0,
      startBendY: Number(stored.bendY) || 0,
      childX: child.x,
      childY: child.y,
      childW: child.w,
      childH: child.h,
      parentX: parent.x,
      parentY: parent.y,
      parentW: parent.w,
      parentH: parent.h,
    });
  };

  const beginBranchDrag = (event, edge) => {
    const parentBox = branchDragBox(edge.toId);
    if (!parentBox) return;
    event.preventDefault();
    trackPointer({
      kind: "node",
      id: edge.toId,
      edgeKey: edge.key,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      editing: false,
      focus: null,
      layout,
      parentBox,
    });
  };

  const beginLinkDrag = (event, link, options = {}) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const scale = zoomRef.current || 1;
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;
    trackPointer({
      kind: "link",
      id: link.id,
      part: options.part || linkDragTarget(link.points, x, y),
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      fromButton: Boolean(options.fromButton),
      route: link.route?.fromSide ? link.route : null,
    });
  };

  const beginLabelDrag = (event, label) => {
    event.preventDefault();
    const chip = event.currentTarget.closest(".flow-label");
    const remove = chip?.querySelector(".flow-label-remove");
    let extra = 0;
    if (remove) {
      const style = window.getComputedStyle(remove);
      if (style.display !== "none") {
        extra = remove.offsetWidth + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
      }
    }
    const stored = labels.find((item) => item.id === label.id) || label;
    trackPointer({
      kind: "label",
      id: label.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: stored.x,
      originY: stored.y,
      boxW: Math.max(20, (chip?.offsetWidth || 72) - extra),
      boxH: chip?.offsetHeight || 24,
      moved: false,
    });
  };

  const beginFreeDrag = (event, node) => {
    const text = event.currentTarget.querySelector?.(".flow-node-text");
    const onText = text && (event.target === text || text.contains(event.target));
    if (onText && document.activeElement === text) return;
    event.preventDefault();
    const stored = nodes.find((item) => item.id === node.id) || node;
    trackPointer({
      kind: "free",
      id: node.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: Number(stored.x) || 0,
      originY: Number(stored.y) || 0,
      boxW: node.w,
      boxH: node.h,
      moved: false,
      focus: onText ? text : null,
    });
  };

  const beginNoteDrag = (event, note) => {
    const field = event.target.closest?.(".flow-note-body, .flow-note-header");
    if (field && (field === document.activeElement || field.contains(document.activeElement))) return;
    event.preventDefault();
    const stored = notes.find((item) => item.id === note.id) || note;
    trackPointer({
      kind: "note",
      id: note.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: stored.x,
      originY: stored.y,
      boxW: note.w,
      boxH: note.h,
      moved: false,
      focus: field,
    });
  };

  const beginNoteResize = (event, note) => {
    event.preventDefault();
    const stored = notes.find((item) => item.id === note.id) || note;
    trackPointer({
      kind: "note-resize",
      id: note.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: stored.x,
      originY: stored.y,
      originW: stored.w,
      originH: stored.h,
      moved: false,
    });
  };

  const applyTheme = (id) => {
    const theme = FLOW_THEMES.find((item) => item.id === id);
    if (!theme) return;
    setThemeId(id);
    setNodes((prev) => prev.map((node) => {
      if (node.free) return node;
      const style = themeLevelStyle(theme, nodeLevel(prev, node.id));
      if (!style) return node;
      return { ...node, fill: style.fill, borderColor: style.border };
    }));
    setNotes((prev) => prev.map((note, index) => {
      const colors = noteColorsForTheme(theme, index);
      return {
        ...note,
        sections: note.sections.map((section) => ({ ...section, ...colors })),
      };
    }));
    markDirty();
  };

  const addLabel = () => {
    const shift = laid.shift || { x: 0, y: 0 };
    const id = newFlowId("a");
    setLabels((prev) => [
      ...prev,
      {
        id,
        text: "",
        x: 48 - shift.x + prev.length * 18,
        y: 48 - shift.y,
      },
    ]);
    setEditingLabel(id);
    setConnectMode(false);
    setConnectFrom(null);
    markDirty();
  };

  const addFreeBranch = () => {
    const shift = laid.shift || { x: 0, y: 0 };
    setNodes((prev) => {
      const node = createFlowNode(null);
      const freeCount = prev.filter((item) => item.free).length;
      node.free = true;
      node.x = 340 - shift.x + freeCount * 48;
      node.y = laid.height - shift.y + 16;
      return [...prev, node];
    });
    setConnectMode(false);
    setConnectFrom(null);
    setSpanMode(false);
    markDirty();
  };

  const createNoteSection = (colors) => ({
    id: newFlowId("s"),
    header: "Header",
    body: "",
    headerFill: colors?.headerFill || "#6b7280",
    bodyFill: colors?.bodyFill || "#ffffff",
  });

  const addNote = () => {
    const shift = laid.shift || { x: 0, y: 0 };
    const id = newFlowId("c");
    const theme = FLOW_THEMES.find((item) => item.id === themeId);
    setNotes((prev) => [
      ...prev,
      {
        id,
        x: 8 - shift.x + prev.length * 24,
        y: laid.height - shift.y + 24,
        w: 280,
        h: 220,
        flow: "vertical",
        design: "card",
        sections: [createNoteSection(noteColorsForTheme(theme, prev.length))],
      },
    ]);
    setNoteFor(id);
    setNoteSection(null);
    setSettingsFor(null);
    setLineEdit(null);
    setConnectMode(false);
    setConnectFrom(null);
    setSpanMode(false);
    markDirty();
  };

  const addNoteSection = (id, flow) => {
    const note = notes.find((item) => item.id === id);
    if (!note) return;
    const source = note.sections.find((section) => section.id === noteSection) || note.sections[note.sections.length - 1];
    const added = {
      id: newFlowId("s"),
      header: "Header",
      body: "",
      headerFill: source?.headerFill || "#6b7280",
      bodyFill: source?.bodyFill || "#ffffff",
    };
    setNotes((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      const sections = [...item.sections, added];
      return {
        ...item,
        flow,
        sections,
        h: flow === "vertical" ? Math.max(item.h, 150 * sections.length) : item.h,
        w: flow === "horizontal" ? Math.max(item.w, 210 * sections.length) : item.w,
      };
    }));
    setNoteSection(added.id);
    markDirty();
  };

  const updateNote = (id, patch) => {
    setNotes((prev) => prev.map((note) => (note.id === id ? { ...note, ...patch } : note)));
    markDirty();
  };

  const updateNoteSection = (noteId, sectionId, patch) => {
    setNotes((prev) => prev.map((note) => (
      note.id === noteId
        ? {
          ...note,
          sections: note.sections.map((section) => (
            section.id === sectionId ? { ...section, ...patch } : section
          )),
        }
        : note
    )));
    markDirty();
  };

  const removeNoteSection = (noteId, sectionId) => {
    setNotes((prev) => prev.flatMap((note) => {
      if (note.id !== noteId) return [note];
      const sections = note.sections.filter((section) => section.id !== sectionId);
      if (!sections.length) return [];
      return [{ ...note, sections }];
    }));
    setNoteSection(null);
    markDirty();
  };

  const addChild = (parentId) => {
    setNodes((prev) => {
      const parent = prev.find((node) => node.id === parentId);
      const child = createFlowNode(parentId);
      const theme = FLOW_THEMES.find((item) => item.id === themeId);
      const style = themeLevelStyle(theme, nodeLevel(prev, parentId) + 1);
      if (style) {
        child.fill = style.fill;
        child.borderColor = style.border;
      }
      if (parent?.out) {
        child.out = parent.out;
        child.branch = parent.out;
      }
      return [...prev, child];
    });
    markDirty();
  };

  const removeNode = (id) => {
    const drop = new Set();
    const collect = (nodeId) => {
      drop.add(nodeId);
      nodes.filter((node) => node.parentId === nodeId).forEach((node) => collect(node.id));
    };
    collect(id);
    setNodes((prev) => prev.filter((node) => !drop.has(node.id)));
    setLinks((prev) => prev.filter((link) => !drop.has(link.fromId) && !drop.has(link.toId)));
    setEdges((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const [fromId, toId] = key.split(":");
        if (!drop.has(fromId) && !drop.has(toId)) next[key] = value;
      });
      return next;
    });
    setSettingsFor(null);
    markDirty();
  };

  const updateEdge = (key, patch) => {
    setEdges((prev) => ({
      ...prev,
      [key]: { text: "", pattern: "solid", thickness: 1.5, ...prev[key], ...patch },
    }));
    markDirty();
  };

  const updateLink = (id, patch) => {
    setLinks((prev) => prev.map((link) => (link.id === id ? { ...link, ...patch } : link)));
    markDirty();
  };

  const removeLink = (id) => {
    setLinks((prev) => prev.filter((link) => link.id !== id));
    setLineEdit(null);
    markDirty();
  };

  const pickConnect = (id) => {
    if (!connectFrom) {
      setConnectFrom(id);
      return;
    }
    if (connectFrom === id) return;
    setLinks((prev) => {
      const exists = prev.some(
        (link) =>
          (link.fromId === connectFrom && link.toId === id) ||
          (link.fromId === id && link.toId === connectFrom)
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          id: newFlowId("l"),
          fromId: connectFrom,
          toId: id,
          text: "",
          pattern: "solid",
          thickness: 1.5,
        },
      ];
    });
    setConnectFrom(null);
    setConnectMode(false);
    markDirty();
  };

  const refreshSaved = async () => {
    const rows = await listFlowDiagrams();
    setSaved(Array.isArray(rows) ? rows : []);
  };

  const publishUpdate = (record) => {
    const nextSvg = renderFlowSvg(record);
    window.dispatchEvent(
      new CustomEvent("rte-flow-updated", {
        detail: { id: record.id, svg: nextSvg, updatedAt: record.updatedAt },
      })
    );
    return nextSvg;
  };

  const persist = async (intent, titleOverride) => {
    setSaving(true);
    try {
      const nextTitle = String(titleOverride != null ? titleOverride : titleDraft).trim() || "Untitled diagram";
      const payload = {
        title: nextTitle,
        layout,
        document: { nodes, edges, links, labels, notes, theme: themeId || null },
      };
      const savedRow = diagramId && isOwner
        ? await updateFlowDiagram(diagramId, payload)
        : await createFlowDiagram(payload);
      const record = { ...savedRow, layout, document: payload.document };
      setDiagramId(savedRow.id);
      setTitle(savedRow.title);
      setIsOwner(true);
      setDirty(false);
      savedSnapRef.current = diagramSnap;
      const nextSvg = publishUpdate(record);
      await refreshSaved();
      setTitleOpen(false);
      const replacingEmbed = embedId && String(savedRow.id) !== String(embedId);
      if (intent === "insert" || replacingEmbed) {
        onApply(savedRow, nextSvg, { close: intent === "insert" });
      }
    } catch (err) {
      toast.error(err.message || "Could not save diagram");
    } finally {
      setSaving(false);
    }
  };

  const openTitle = (intent) => {
    setTitleDraft(title || "");
    setTitleIntent(intent);
    setTitleOpen(true);
  };

  const handleSave = () => {
    if (diagramId && isOwner) {
      if (!dirty || saving) return;
      persist("save", title);
      return;
    }
    openTitle("save");
  };

  const handleAdd = () => {
    if (phase !== "edit" || saving) return;
    if (diagramId && isOwner && !dirty) {
      const record = { id: diagramId, title, layout, document: { nodes, edges, links, labels, notes, theme: themeId || null }, isOwner: true };
      const nextSvg = renderFlowSvg(record);
      window.dispatchEvent(new CustomEvent("rte-flow-updated", {
        detail: { id: record.id, svg: nextSvg },
      }));
      onApply(record, nextSvg);
      return;
    }
    if (diagramId && isOwner) {
      persist("insert", title);
      return;
    }
    openTitle("insert");
  };

  const removeSaved = async (row, event) => {
    event.stopPropagation();
    if (!window.confirm(`Delete "${row.title}"? Places that use it will show it as unavailable.`)) return;
    try {
      await deleteFlowDiagram(row.id);
      setSaved((prev) => prev.filter((item) => item.id !== row.id));
      window.dispatchEvent(new CustomEvent("rte-flow-removed", { detail: { id: row.id } }));
      if (diagramId === row.id) {
        setPhase("pick");
        setDiagramId(null);
        setDirty(false);
      }
    } catch (err) {
      toast.error(err.message || "Could not delete diagram");
    }
  };

  const activeNode = laid.nodes.find((node) => node.id === settingsFor);
  const activeNote = (laid.notes || []).find((note) => note.id === noteFor) || null;
  const activeSection = activeNote?.sections.find((section) => section.id === noteSection) || activeNote?.sections[0] || null;
  const activeTheme = FLOW_THEMES.find((item) => item.id === themeId);
  const noteThemePairs = [];
  (activeTheme?.levels || []).forEach((_, index) => {
    const pair = noteColorsForTheme(activeTheme, index);
    if (noteThemePairs.some((item) => item.headerFill === pair.headerFill && item.bodyFill === pair.bodyFill)) return;
    noteThemePairs.push(pair);
  });
  const showNoteColors = !noteThemePairs.length || noteColorDetail === activeSection?.id;
  const activeEdge = lineEdit?.kind === "tree"
    ? laid.treeEdges.find((edge) => edge.key === lineEdit.key)
    : laid.links.find((link) => link.id === lineEdit?.id);
  const diagramHist = historyRef.current;
  const diagramPending = historyTick >= 0 && diagramHist.index >= 0 && diagramSnap !== diagramHist.stack[diagramHist.index];
  const canDiagramUndo = diagramHist.index > 0 || diagramPending;
  const canDiagramRedo = !diagramPending && diagramHist.index >= 0 && diagramHist.index < diagramHist.stack.length - 1;

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="flow-dialog"
        contentClassName="flow-modal"
        fullscreen
        enforceFocus={false}
      >
        <div className="flow-shell">
          <section className="flow-main">
            <div className="flow-main-head">Create a diagram or flowchart</div>
            {phase === "pick" ? (
              <div className="flow-picker">
                <div className="flow-picker-title">Choose a diagram to start</div>
                <div className="radio-inputs flow-type-row" role="radiogroup" aria-label="Diagram layout">
                  {FLOW_LAYOUTS.map((item) => (
                    <label key={item.id} className="radio">
                      <input
                        type="radio"
                        name="flow-start-layout"
                        onChange={() => startLayout(item.id)}
                        aria-label={item.label}
                      />
                      <span className="name"><TypeIcon type={item.id} /></span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="flow-canvas-bar">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={backToPicker}>New</button>
                  <span className={`flow-save-badge ms-auto${diagramId && isOwner && !dirty ? " is-saved" : " is-dirty"}`} role="status">
                    {diagramId && isOwner && !dirty ? "Saved" : "Not saved"}
                  </span>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleSave} disabled={saving}>
                    Save
                  </button>
                </div>
                {!isOwner && diagramId ? (
                  <p className="flow-copy-note">
                    This diagram belongs to someone else. Saving stores a copy on your account and updates only this insert.
                  </p>
                ) : null}
                <div className="flow-canvas-wrap border-top">
                  <div className="flow-canvas-hud">
                    <div className="flow-zoom" role="group" aria-label="Zoom">
                      <button type="button" className="btn btn-sm btn-light" aria-label="Zoom in" onClick={() => changeZoom(zoom + 0.1)}>+</button>
                      <span className="flow-zoom-value">{Math.round(zoom * 100)}%</span>
                      <button type="button" className="btn btn-sm btn-light" aria-label="Zoom out" onClick={() => changeZoom(zoom - 0.1)}>−</button>
                    </div>
                    <div className="flow-history" role="group" aria-label="History">
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        aria-label="Undo (Ctrl+Z)"
                        title="Undo (Ctrl+Z)"
                        disabled={!canDiagramUndo}
                        onClick={performDiagramUndo}
                      >
                        <i className="bi bi-arrow-90deg-left" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        aria-label="Redo (Ctrl+Y)"
                        title="Redo (Ctrl+Y)"
                        disabled={!canDiagramRedo}
                        onClick={performDiagramRedo}
                      >
                        <i className="bi bi-arrow-90deg-right" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <div className={`flow-canvas-tools${toolsOpen ? " is-open" : ""}`} role="group" aria-label="Diagram menu">
                    <button
                      type="button"
                      className={`flow-tools-toggle${toolsOpen ? " is-active" : ""}`}
                      aria-expanded={toolsOpen}
                      aria-label={toolsOpen ? "Close menu" : "Open menu"}
                      onClick={() => setToolsOpen((open) => !open)}
                    >
                      <i className={`bi ${toolsOpen ? "bi-x-lg" : "bi-list"}`} aria-hidden="true" />
                    </button>
                    {toolsOpen ? (
                      <>
                        <button
                          type="button"
                          className="flow-tools-action"
                          aria-label="Label"
                          onClick={addLabel}
                        >
                          <i className="bi bi-tag" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="flow-tools-action"
                          aria-label="Add branch"
                          onClick={addFreeBranch}
                        >
                          <i className="bi bi-plus-square" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="flow-tools-action"
                          aria-label="Add text box"
                          onClick={addNote}
                        >
                          <i className="bi bi-card-text" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={`flow-tools-action${connectMode ? " is-active" : ""}`}
                          aria-label="Connect"
                          onClick={() => {
                            setConnectMode((on) => !on);
                            setSpanMode(false);
                            setConnectFrom(null);
                            setSettingsFor(null);
                            setLineEdit(null);
                          }}
                        >
                          <i className="bi bi-bezier2" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className={`flow-tools-action${spanMode ? " is-active" : ""}`}
                          aria-label="Resize connectors"
                          aria-pressed={spanMode}
                          onClick={() => {
                            setSpanMode((on) => !on);
                            setConnectMode(false);
                            setConnectFrom(null);
                            setSettingsFor(null);
                            setLineEdit(null);
                          }}
                        >
                          <i className="bi bi-arrows-expand" aria-hidden="true" />
                        </button>
                        {FLOW_THEMES.map((theme) => (
                          <button
                            key={theme.id}
                            type="button"
                            aria-label={theme.label}
                            aria-pressed={themeId === theme.id}
                            className={`flow-theme-option${themeId === theme.id ? " is-active" : ""}`}
                            onClick={() => applyTheme(theme.id)}
                          >
                            <span className="flow-theme-swatches" aria-hidden="true">
                              {theme.levels.slice(0, 3).map((level, index) => (
                                <span
                                  key={`${theme.id}-${index}`}
                                  className="flow-theme-swatch"
                                  style={{ background: level.fill }}
                                />
                              ))}
                            </span>
                          </button>
                        ))}
                      </>
                    ) : null}
                  </div>
                  {connectMode ? (
                    <div className="flow-connect-hint">
                      {connectFrom ? "Now choose the node to connect to." : "Choose the first node, then the second."}
                    </div>
                  ) : null}
                  {spanMode ? (
                    <div className="flow-connect-hint">Drag a connector to stretch it. Drag it back to snap.</div>
                  ) : null}
                  <div
                    className={`flow-canvas-scroll${panning ? " is-panning" : ""}`}
                    ref={scrollRef}
                    onPointerDown={beginPan}
                  >
                    <div
                      className="flow-stage-scale"
                      style={{
                        width: `max(100%, ${laid.width * zoom}px)`,
                        height: `max(100%, ${laid.height * zoom}px)`,
                      }}
                    >
                    <div className="flow-stage-frame" style={{ width: laid.width * zoom, height: laid.height * zoom }}>
                    <div
                      ref={stageRef}
                      className={`flow-stage${connectMode ? " is-connecting" : ""}${spanMode ? " is-span" : ""}${dragKind === "node" ? " is-branch-drag" : ""}${dragKind === "link" ? " is-link-drag" : ""}${dragKind === "span" ? " is-span-drag" : ""}${dragKind === "free" ? " is-free-drag" : ""}${dragKind === "note" || dragKind === "note-resize" ? " is-note-drag" : ""}`}
                      style={{ width: laid.width, height: laid.height, transform: `scale(${zoom})` }}
                    >
                      <div className="flow-stage-svg" dangerouslySetInnerHTML={{ __html: svg }} />
                      {snapGuides?.vertical || snapGuides?.horizontal ? (
                        <svg className="flow-snap-guides" width={laid.width} height={laid.height} aria-hidden="true">
                          {snapGuides.vertical ? (
                            <line x1={snapGuides.vertical.x} y1={snapGuides.vertical.y1} x2={snapGuides.vertical.x} y2={snapGuides.vertical.y2} />
                          ) : null}
                          {snapGuides.horizontal ? (
                            <line x1={snapGuides.horizontal.x1} y1={snapGuides.horizontal.y} x2={snapGuides.horizontal.x2} y2={snapGuides.horizontal.y} />
                          ) : null}
                        </svg>
                      ) : null}
                      {laid.links.length || (layout !== "radial" && laid.treeEdges.length) ? (
                        <svg className="flow-link-layer" width={laid.width} height={laid.height} aria-hidden="true">
                          {layout !== "radial" ? laid.treeEdges.map((edge) => (
                            <polyline
                              key={edge.key}
                              className="flow-branch-hit"
                              points={edge.points.map((point) => `${point.x},${point.y}`).join(" ")}
                              onPointerDown={(event) => {
                                if (connectMode) return;
                                event.stopPropagation();
                                setSettingsFor(null);
                                setLineEdit(null);
                                if (spanMode) {
                                  beginSpanDrag(event, edge);
                                  return;
                                }
                                beginBranchDrag(event, edge);
                              }}
                            />
                          )) : null}
                          {laid.links.map((link) => (
                            <polyline
                              key={link.id}
                              className="flow-link-hit"
                              points={link.points.map((point) => `${point.x},${point.y}`).join(" ")}
                              onPointerDown={(event) => {
                                if (connectMode) return;
                                event.preventDefault();
                                event.stopPropagation();
                                setSettingsFor(null);
                                setLineEdit(null);
                                beginLinkDrag(event, link);
                              }}
                            />
                          ))}
                        </svg>
                      ) : null}
                      {laid.nodes.map((node) => {
                        const draggable = (Boolean(node.parentId) || node.free) && !connectMode;
                        return (
                        <div
                          key={node.id}
                          className={`flow-node-editor${connectFrom === node.id ? " is-connect-from" : ""}${draggable ? " is-draggable" : ""}${dragKind === "node" && nudge?.id === node.id ? " is-dragging" : ""}`}
                          style={{
                            left: node.x,
                            top: node.y,
                            width: node.w,
                            height: node.h,
                            color: textColorForFill(node.fill),
                          }}
                          onPointerDown={(event) => {
                            if (event.target.closest(".flow-node-gear")) return;
                            if (connectMode) {
                              event.preventDefault();
                              event.stopPropagation();
                              pickConnect(node.id);
                              return;
                            }
                            if (!draggable) return;
                            if (node.free) {
                              beginFreeDrag(event, node);
                              return;
                            }
                            beginNodeDrag(event, node);
                          }}
                        >
                          <textarea
                            className="flow-node-text"
                            style={{
                              left: node.textBox.x,
                              top: node.textBox.y,
                              width: node.textBox.w,
                              height: node.textBox.h,
                            }}
                            value={node.text}
                            readOnly={connectMode}
                            placeholder="Text"
                            aria-label="Branch text"
                            onChange={(event) => updateNode(node.id, { text: event.target.value.slice(0, 240) })}
                          />
                          <Button
                            variant="light"
                            className="flow-node-gear"
                            aria-label="Branch settings"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={() => { setSettingsFor(node.id); setNoteFor(null); setLineEdit(null); }}
                          >
                            <i className="bi bi-gear" aria-hidden="true" />
                          </Button>
                        </div>
                        );
                      })}
                      {laid.links.map((link) => {
                        const fromHandle = linkEndHandle(link.points[0], link.points[1]);
                        const toHandle = linkEndHandle(link.points[link.points.length - 1], link.points[link.points.length - 2]);
                        return (
                          <React.Fragment key={`ends-${link.id}`}>
                            <button
                              type="button"
                              className="flow-link-end"
                              style={{ left: fromHandle.x, top: fromHandle.y }}
                              aria-label="Drag connection start"
                              onPointerDown={(event) => {
                                if (connectMode) return;
                                event.preventDefault();
                                event.stopPropagation();
                                setSettingsFor(null);
                                setLineEdit(null);
                                beginLinkDrag(event, link, { part: "from" });
                              }}
                            />
                            <button
                              type="button"
                              className="flow-link-end"
                              style={{ left: toHandle.x, top: toHandle.y }}
                              aria-label="Drag connection end"
                              onPointerDown={(event) => {
                                if (connectMode) return;
                                event.preventDefault();
                                event.stopPropagation();
                                setSettingsFor(null);
                                setLineEdit(null);
                                beginLinkDrag(event, link, { part: "to" });
                              }}
                            />
                          </React.Fragment>
                        );
                      })}
                      {laid.nodes.filter((node) => node.plus).map((node) => (
                        <Button
                          key={`plus-${node.id}`}
                          variant="primary"
                          className="flow-plus"
                          style={{ left: node.plus.x, top: node.plus.y }}
                          onClick={() => addChild(node.id)}
                        >
                          <i className="bi bi-plus" aria-hidden="true" />
                        </Button>
                      ))}
                      {[...laid.treeEdges, ...laid.links].map((edge) => (
                        <Button
                          key={edge.key || edge.id}
                          variant="light"
                          className="flow-line-btn"
                          style={{ left: edge.mid.x, top: edge.mid.y }}
                          aria-label={edge.text ? `Edit line label ${edge.text}` : "Edit line"}
                          onPointerDown={(event) => {
                            if (edge.key || connectMode) return;
                            event.stopPropagation();
                            event.preventDefault();
                            setSettingsFor(null);
                            setLineEdit(null);
                            beginLinkDrag(event, edge, { fromButton: true });
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={() => {
                            if (skipClickRef.current) {
                              skipClickRef.current = false;
                              return;
                            }
                            setLineEdit(edge.key ? { kind: "tree", key: edge.key } : { kind: "link", id: edge.id });
                            setSettingsFor(null);
                          }}
                        >
                          {edge.text ? <span>{edge.text}</span> : <i className="bi bi-plus" aria-hidden="true" />}
                        </Button>
                      ))}
                      {(laid.labels || []).map((label) => (
                        <div
                          key={label.id}
                          className={`flow-label${dragKind === "label" ? " is-dragging" : ""}`}
                          style={{ left: label.x, top: label.y }}
                        >
                          <button
                            type="button"
                            className="flow-label-grip"
                            aria-label="Move label"
                            onPointerDown={(event) => {
                              if (connectMode) return;
                              event.stopPropagation();
                              beginLabelDrag(event, label);
                            }}
                          >
                            <i className="bi bi-grip-vertical" aria-hidden="true" />
                          </button>
                          <input
                            className="flow-label-text"
                            data-label-id={label.id}
                            size={Math.max(1, (label.text || "Label").length)}
                            value={label.text}
                            placeholder="Label"
                            aria-label="Diagram label"
                            onChange={(event) => {
                              const text = event.target.value.slice(0, 80);
                              setLabels((prev) => prev.map((item) => (item.id === label.id ? { ...item, text } : item)));
                              markDirty();
                            }}
                            onFocus={() => setEditingLabel(label.id)}
                          />
                          <button
                            type="button"
                            className="flow-label-remove"
                            aria-label="Remove label"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={() => {
                              setLabels((prev) => prev.filter((item) => item.id !== label.id));
                              setEditingLabel(null);
                              markDirty();
                            }}
                          >
                            <i className="bi bi-x" aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                      {(laid.notes || []).map((note) => {
                        const design = ["plain", "sidebar", "outline", "note"].includes(note.design) ? note.design : "card";
                        const frame = note.sections[0]?.headerFill || "#6b7280";
                        return (
                        <div
                          key={note.id}
                          className={`flow-note${noteFor === note.id ? " is-selected" : ""}`}
                          style={{ left: note.x, top: note.y, width: note.w, height: note.h }}
                          onPointerDown={(event) => {
                            if (event.target.closest("button, .flow-note-resize")) return;
                            const section = event.target.closest("[data-section-id]");
                            if (section) setNoteSection(section.getAttribute("data-section-id"));
                            if (connectMode) return;
                            event.stopPropagation();
                            beginNoteDrag(event, note);
                          }}
                        >
                          <div
                            className={`flow-note-sections is-${note.flow} is-${design}`}
                            style={design === "outline" ? { borderColor: frame } : undefined}
                          >
                            {note.sections.map((section) => {
                              const paper = design === "note" ? section.headerFill : section.bodyFill;
                              const title = design === "card" || design === "note" ? section.headerFill : paper;
                              return (
                              <div
                                key={section.id}
                                className={`flow-note-section${noteFor === note.id && (noteSection || note.sections[0]?.id) === section.id ? " is-active" : ""}`}
                                data-section-id={section.id}
                              >
                                {design === "sidebar" ? <span className="flow-note-rail" style={{ background: section.headerFill }} /> : null}
                                <div
                                  className="flow-note-head"
                                  style={{
                                    background: title,
                                    color: design === "outline" || design === "plain" ? section.headerFill : textColorForFill(title),
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="flow-note-grip"
                                    aria-label="Move text box"
                                    onPointerDown={(event) => {
                                      event.stopPropagation();
                                      beginNoteDrag(event, note);
                                    }}
                                  >
                                    <i className="bi bi-grip-vertical" aria-hidden="true" />
                                  </button>
                                  <NoteRichText
                                    className="flow-note-header"
                                    value={section.header}
                                    placeholder="Header"
                                    ariaLabel="Text box header"
                                    maxLength={80}
                                    singleLine
                                    onChange={(header) => updateNoteSection(note.id, section.id, { header })}
                                    onFocus={() => setNoteSection(section.id)}
                                  />
                                </div>
                                <NoteRichText
                                  className="flow-note-body"
                                  style={{
                                    background: paper,
                                    color: textColorForFill(paper),
                                  }}
                                  value={section.body}
                                  placeholder="Text"
                                  ariaLabel="Text box body"
                                  maxLength={2000}
                                  onChange={(body) => updateNoteSection(note.id, section.id, { body })}
                                  onFocus={() => setNoteSection(section.id)}
                                />
                              </div>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            className="flow-note-gear"
                            aria-label="Text box settings"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={() => {
                              setNoteFor(note.id);
                              setNoteSection((current) => (
                                note.sections.some((section) => section.id === current)
                                  ? current
                                  : (note.sections[note.sections.length - 1]?.id || null)
                              ));
                              setSettingsFor(null);
                              setLineEdit(null);
                            }}
                          >
                            <i className="bi bi-gear" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="flow-note-remove"
                            aria-label="Remove text box"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={() => {
                              setNotes((prev) => prev.filter((item) => item.id !== note.id));
                              if (noteFor === note.id) setNoteFor(null);
                              markDirty();
                            }}
                          >
                            <i className="bi bi-x" aria-hidden="true" />
                          </button>
                          <div
                            className="flow-note-resize"
                            role="separator"
                            aria-label="Resize text box"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              beginNoteResize(event, note);
                            }}
                          />
                        </div>
                        );
                      })}
                      {activeNote && activeSection ? (
                        <div
                          className="flow-pop flow-note-pop"
                          style={{
                            left: activeNote.x + activeNote.w + 12 + 268 > laid.width
                              ? Math.max(8, activeNote.x - 280)
                              : activeNote.x + activeNote.w + 12,
                            top: Math.max(8, activeNote.y),
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <div className="flow-pop-label">Design</div>
                          <FlowRadios
                            className="flow-note-design"
                            name={`flow-note-design-${activeNote.id}`}
                            value={["plain", "sidebar", "outline", "note"].includes(activeNote.design) ? activeNote.design : "card"}
                            options={NOTE_DESIGNS}
                            onChange={(design) => updateNote(activeNote.id, { design })}
                          />
                          {noteThemePairs.length ? (
                            <>
                              <div className="flow-pop-label">Theme</div>
                              <div className="flow-note-colors">
                                {noteThemePairs.map((pair, index) => {
                                  const selected = pair.headerFill === activeSection.headerFill && pair.bodyFill === activeSection.bodyFill;
                                  return (
                                    <button
                                      key={`${pair.headerFill}-${pair.bodyFill}`}
                                      type="button"
                                      className={`flow-note-pair${selected ? " is-active" : ""}`}
                                      aria-label={`Theme colors ${index + 1}`}
                                      aria-pressed={selected}
                                      onClick={() => {
                                        updateNoteSection(activeNote.id, activeSection.id, {
                                          headerFill: pair.headerFill,
                                          bodyFill: pair.bodyFill,
                                        });
                                        setNoteColorDetail(activeSection.id);
                                      }}
                                    >
                                      <span style={{ background: pair.headerFill }} />
                                      <span style={{ background: pair.bodyFill }} />
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          ) : null}
                          {showNoteColors ? (
                            <>
                              <div className="flow-pop-label">
                                {activeNote.design === "plain" ? "Title color" : activeNote.design === "sidebar" ? "Bar color" : activeNote.design === "outline" ? "Border color" : activeNote.design === "note" ? "Color" : "Header color"}
                              </div>
                              <div className="flow-note-colors">
                                {NOTE_COLORS.map((color) => (
                                  <button
                                    key={`header-${color}`}
                                    type="button"
                                    className={`flow-note-color${activeSection.headerFill === color ? " is-active" : ""}`}
                                    style={{ background: color }}
                                    aria-label={`Header color ${color}`}
                                    onClick={() => updateNoteSection(activeNote.id, activeSection.id, { headerFill: color })}
                                  />
                                ))}
                              </div>
                              {activeNote.design === "note" ? null : (
                                <>
                                  <div className="flow-pop-label">{activeNote.design === "card" || !activeNote.design ? "Body color" : "Fill"}</div>
                                  <div className="flow-note-colors">
                                    {NOTE_COLORS.map((color) => (
                                      <button
                                        key={`body-${color}`}
                                        type="button"
                                        className={`flow-note-color${activeSection.bodyFill === color ? " is-active" : ""}`}
                                        style={{ background: color }}
                                        aria-label={`Body color ${color}`}
                                        onClick={() => updateNoteSection(activeNote.id, activeSection.id, { bodyFill: color })}
                                      />
                                    ))}
                                  </div>
                                </>
                              )}
                            </>
                          ) : null}
                          <div className="flow-pop-label">Add another box</div>
                          <div className="flow-note-actions">
                            <button type="button" className="btn btn-sm btn-light" onClick={() => addNoteSection(activeNote.id, "vertical")}>
                              Join below
                            </button>
                            <button type="button" className="btn btn-sm btn-light" onClick={() => addNoteSection(activeNote.id, "horizontal")}>
                              Join beside
                            </button>
                          </div>
                          {activeNote.sections.length > 1 ? (
                            <button
                              type="button"
                              className="btn btn-link-dng flow-pop-danger"
                              onClick={() => removeNoteSection(activeNote.id, activeSection.id)}
                            >
                              Remove this section
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-link-dng flow-pop-danger"
                              onClick={() => {
                                setNotes((prev) => prev.filter((item) => item.id !== activeNote.id));
                                setNoteFor(null);
                                markDirty();
                              }}
                            >
                              Remove text box
                            </button>
                          )}
                        </div>
                      ) : null}
                      {activeNode ? (
                        <div
                          className="flow-pop"
                          style={{
                            left: activeNode.x + activeNode.w + 12 + 268 > laid.width
                              ? Math.max(8, activeNode.x - 280)
                              : activeNode.x + activeNode.w + 12,
                            top: Math.max(8, activeNode.y),
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                        >
                          <div className="flow-pop-label">Shape</div>
                          <div className="radio-inputs flow-shape-radios" role="radiogroup" aria-label="Shape">
                            {FLOW_SHAPES.map((shape) => (
                              <label key={shape.id} className="radio">
                                <input
                                  type="radio"
                                  name={`flow-shape-${activeNode.id}`}
                                  checked={(activeNode.shape || "rounded") === shape.id}
                                  onChange={() => updateNode(activeNode.id, { shape: shape.id })}
                                  aria-label={shape.label}
                                />
                                <span className="name"><ShapeGlyph shape={shape.id} /></span>
                              </label>
                            ))}
                          </div>
                          <div className="flow-pop-label">Color</div>
                          <div className="d-flex flex-row gap-2">
                          <div className="flow-schemes">
                            {FLOW_SCHEMES.map((scheme) => (
                              <button
                                key={scheme.id}
                                type="button"
                                className={`flow-scheme${activeNode.fill === scheme.fill && activeNode.borderColor === scheme.border ? " is-active" : ""}`}
                                style={{ background: scheme.fill, borderColor: scheme.border }}
                                aria-label={scheme.label}
                                onClick={() => updateNode(activeNode.id, { fill: scheme.fill, borderColor: scheme.border })}
                              />
                            ))}
                          </div>
                          <div className="flow-color-row">
                            <input
                              type="color"
                              aria-label="Custom fill color"
                              value={toColorInput(activeNode.fill)}
                              onChange={(event) => updateNode(activeNode.id, { fill: event.target.value })}
                            />
                            <input
                              type="color"
                              aria-label="Custom border color"
                              value={toColorInput(activeNode.borderColor)}
                              onChange={(event) => updateNode(activeNode.id, { borderColor: event.target.value })}
                            />
                          </div>
                          </div>
                          {layout !== "radial" ? (
                            <>
                              <div className="flow-pop-label">Direction</div>
                              <FlowRadios
                                name={`flow-dir-${activeNode.id}`}
                                value={activeNode.out ? "horizontal" : "vertical"}
                                options={[
                                  { id: "vertical", label: layout === "leftRight" ? "Horizontal" : "Vertical" },
                                  { id: "horizontal", label: layout === "leftRight" ? "Vertical" : "Horizontal" },
                                ]}
                                onChange={(value) => updateNode(activeNode.id, {
                                  out: value === "vertical" ? null : sideDirection(
                                    layout,
                                    activeNode,
                                    laid.nodes.find((node) => node.id === activeNode.parentId)
                                  ),
                                })}
                              />
                            </>
                          ) : null}
                          <div className="flow-pop-label">Thickness</div>
                          <div className="flow-thickness-row">
                            <input
                              type="range"
                              min="1"
                              max="6"
                              step="0.5"
                              aria-label="Border thickness"
                              value={activeNode.borderWidth}
                              onChange={(event) => updateNode(activeNode.id, { borderWidth: Number(event.target.value) })}
                            />
                            <span>{activeNode.borderWidth}</span>
                          </div>
                          {activeNode.parentId || activeNode.free ? (
                            <button type="button" className="btn btn-link-dng flow-pop-danger" onClick={() => removeNode(activeNode.id)}>
                              Remove branch
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {activeEdge ? (
                        <div
                          className="flow-pop"
                          style={{
                            left: Math.min(Math.max(8, activeEdge.mid.x + 16), Math.max(8, laid.width - 276)),
                            top: Math.max(8, activeEdge.mid.y + 16),
                          }}
                          onMouseDown={(event) => event.stopPropagation()}
                        >
                          <div className="flow-pop-label">Line text</div>
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            value={activeEdge.text || ""}
                            maxLength={80}
                            aria-label="Line text"
                            onChange={(event) => {
                              const text = event.target.value;
                              if (lineEdit.kind === "tree") updateEdge(lineEdit.key, { text });
                              else updateLink(lineEdit.id, { text });
                            }}
                          />
                          <div className="flow-pop-label">Style</div>
                          <FlowRadios
                            name={`flow-style-${activeEdge.key || activeEdge.id}`}
                            value={activeEdge.pattern || "solid"}
                            options={[
                              { id: "solid", label: "Solid" },
                              { id: "dashed", label: "Dashed" },
                              { id: "dotted", label: "Dotted" },
                            ]}
                            onChange={(pattern) => {
                              if (lineEdit.kind === "tree") updateEdge(lineEdit.key, { pattern });
                              else updateLink(lineEdit.id, { pattern });
                            }}
                          />
                          <div className="flow-pop-label">Color</div>
                          <div className="d-flex flex-row gap-2">
                          <div className="flow-note-colors">
                            {LINE_COLORS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                className={`flow-note-color${(activeEdge.color || "#64748b") === color ? " is-active" : ""}`}
                                style={{ background: color }}
                                aria-label={`Line color ${color}`}
                                onClick={() => {
                                  if (lineEdit.kind === "tree") updateEdge(lineEdit.key, { color });
                                  else updateLink(lineEdit.id, { color });
                                }}
                              />
                            ))}
                          </div>
                          <div className="flow-color-row">
                            <input
                              type="color"
                              aria-label="Custom line color"
                              value={toColorInput(activeEdge.color || "#64748b")}
                              onChange={(event) => {
                                const color = event.target.value;
                                if (lineEdit.kind === "tree") updateEdge(lineEdit.key, { color });
                                else updateLink(lineEdit.id, { color });
                              }}
                            />
                          </div>
                          </div>
                          <div className="flow-pop-label">Arrow head</div>
                          <div className="radio-inputs flow-shape-radios" role="radiogroup" aria-label="Arrow head">
                            {FLOW_ARROW_HEADS.map((head) => {
                              const arrowOn = Boolean(activeEdge.arrow && activeEdge.arrow !== "none");
                              const selected = arrowOn && (activeEdge.arrowHead || "arrow") === head.id;
                              return (
                                <label
                                  key={head.id}
                                  className="radio"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    const patch = selected
                                      ? { arrow: "none" }
                                      : {
                                        arrowHead: head.id,
                                        arrow: arrowOn ? activeEdge.arrow : "end",
                                      };
                                    if (lineEdit.kind === "tree") updateEdge(lineEdit.key, patch);
                                    else updateLink(lineEdit.id, patch);
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`flow-head-${activeEdge.key || activeEdge.id}`}
                                    checked={selected}
                                    onChange={() => {}}
                                    aria-label={head.label}
                                  />
                                  <span className="name"><ArrowGlyph head={head.id} /></span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="flow-pop-label">Arrow ends</div>
                          <FlowRadios
                            name={`flow-arrow-${activeEdge.key || activeEdge.id}`}
                            value={activeEdge.arrow || "none"}
                            options={FLOW_ARROW_ENDS}
                            onChange={(arrow) => {
                              const patch = { arrow };
                              if (lineEdit.kind === "tree") updateEdge(lineEdit.key, patch);
                              else updateLink(lineEdit.id, patch);
                            }}
                          />
                          <div className="flow-pop-label">Thickness</div>
                          <div className="flow-thickness-row">
                            <input
                              type="range"
                              min="1"
                              max="6"
                              step="0.5"
                              aria-label="Line thickness"
                              value={activeEdge.thickness}
                              onChange={(event) => {
                                const thickness = Number(event.target.value);
                                if (lineEdit.kind === "tree") updateEdge(lineEdit.key, { thickness });
                                else updateLink(lineEdit.id, { thickness });
                              }}
                            />
                            <span>{activeEdge.thickness}</span>
                          </div>
                          {lineEdit.kind === "link" ? (
                            <button type="button" className="btn btn-link-dng flow-pop-danger" onClick={() => removeLink(lineEdit.id)}>
                              Remove connection
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
          <aside className="flow-side">
            <div className="flow-side-head">Saved diagrams and flowcharts</div>
            <div className="flow-side-list">
              {loading ? <div className="flow-saved-empty">Loading…</div> : null}
              {!loading && saved.length === 0 ? (
                <div className="flow-saved-empty">No saved diagrams yet.</div>
              ) : null}
              {saved.map((row) => (
                <div key={row.id} className={`flow-saved-item${diagramId === row.id ? " is-active" : ""}`}>
                  <button
                    type="button"
                    className="flow-saved-open"
                    onClick={() => {
                      if (dirty && row.id !== diagramId && !window.confirm("Open this saved diagram? Unsaved changes on the canvas will be lost.")) return;
                      applyRecord(row);
                    }}
                  >
                    <span className="flow-saved-icon"><TypeIcon type={row.layout} /></span>
                    <span className="flow-saved-title">{row.title}</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary flow-saved-delete"
                    aria-label={`Delete ${row.title}`}
                    onClick={(event) => removeSaved(row, event)}
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flow-side-footer">
              <Button variant="light" size="sm" onClick={onHide}>Close</Button>
              <Button variant="primary" size="sm" onClick={handleAdd} disabled={phase !== "edit" || saving}>
                Add diagram
              </Button>
            </div>
          </aside>
        </div>
      </Modal>
      <Modal
        show={titleOpen}
        onHide={() => setTitleOpen(false)}
        centered
        enforceFocus={false}
        className="flow-title-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="h5 mb-0">Save diagram</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label" htmlFor="flow-diagram-title">Title</label>
          <input
            id="flow-diagram-title"
            className="form-control"
            type="text"
            value={titleDraft}
            maxLength={120}
            autoFocus
            onChange={(event) => setTitleDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                persist(titleIntent);
              }
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setTitleOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => persist(titleIntent)}>
            {titleIntent === "insert" ? "Save and add" : "Save"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
